/**
 * TileMesh - Renders a single map tile on the morphing hemisphere.
 *
 * Positions a textured quad at the correct location on the hemisphere/plane
 * based on the tile's geographic bounds and current curvature.
 *
 * Performance: Uses GPU shader-based morphing - geometry is created once per tile,
 * and curvature interpolation happens entirely on the GPU.
 */

import { useMemo, memo, useRef, useEffect } from 'react'
import { useLoader } from '@react-three/fiber'
import { Line, Text } from '@react-three/drei'
import * as THREE from 'three'
import { GLOBE_CONFIG } from '@/domain/globe/constants'
import {
  tiledMorphVertexShader,
  basicMorphVertexShader,
  texturedFragmentShader,
  solidColorWithOpacityFragmentShader,
} from '@/domain/globe/shaders'
import { createTileGeometry, calculateCornerPositions } from '@/domain/globe/utils'
import type { GlobeTile } from '@/domain/globe/types'

interface TileMeshProps {
  /** Tile data with bounds and URL */
  tile: GlobeTile
  /** Current curvature for positioning */
  curvature: number
  /** Radius of the hemisphere */
  radius?: number
  /** Show debug overlay (boundary lines + z/x/y label) */
  showDebugOverlay?: boolean
  /** UV offset for rendering a portion of the texture (fallback tiles) */
  uvOffset?: [number, number]
  /** UV scale for rendering a portion of the texture (fallback tiles) */
  uvScale?: number
  /** Override bounds for rendering (used when showing fallback tile in child's location) */
  renderBounds?: { north: number; south: number; east: number; west: number }
}

/**
 * TileMesh component renders a textured tile on the globe surface.
 *
 * Performance: Geometry is created ONCE per tile. Curvature changes only
 * update a shader uniform, which is extremely fast (no geometry recreation).
 *
 * @example
 * ```tsx
 * {tiles.map(tile => (
 *   <TileMesh
 *     key={getTileKey(tile.coord)}
 *     tile={tile}
 *     curvature={curvature}
 *   />
 * ))}
 * ```
 */
export const TileMesh = memo(function TileMesh({
  tile,
  curvature,
  radius = GLOBE_CONFIG.SPHERE_RADIUS,
  showDebugOverlay = false,
  uvOffset = [0, 0],
  uvScale = 1,
  renderBounds,
}: TileMeshProps) {
  const tileKey = `${tile.coord.z}/${tile.coord.x}/${tile.coord.y}`
  const materialRef = useRef<THREE.ShaderMaterial>(null)

  // Use renderBounds if provided (for fallback tiles), otherwise use tile bounds
  const bounds = renderBounds ?? tile.bounds

  // Load tile texture
  const rawTexture = useLoader(THREE.TextureLoader, tile.url)

  // Configure texture (must happen synchronously before render, not in useEffect)
  const texture = useMemo(() => {
    rawTexture.minFilter = THREE.LinearFilter
    rawTexture.magFilter = THREE.LinearFilter
    rawTexture.colorSpace = THREE.SRGBColorSpace
    return rawTexture
  }, [rawTexture])

  // Create geometry ONCE with both position sets (no curvature dependency!)
  const geometry = useMemo(
    () => createTileGeometry(bounds, radius, 4),
    [bounds, radius]
  )

  // Dispose geometry when component unmounts or geometry changes
  // This prevents GPU memory leaks from accumulated geometries
  useEffect(() => {
    return () => {
      geometry.dispose()
    }
  }, [geometry])

  // Create uniforms object - memoized to keep reference stable
  const uniforms = useMemo(
    () => ({
      curvature: { value: curvature },
      map: { value: texture },
      uvOffset: { value: new THREE.Vector2(uvOffset[0], uvOffset[1]) },
      uvScale: { value: uvScale },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [texture, uvOffset[0], uvOffset[1], uvScale]
  )

  // Update curvature uniform when it changes (no geometry recreation!)
  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.curvature.value = curvature
    }
  }, [curvature])

  // Calculate corner positions for debug overlay
  const corners = useMemo(() => {
    if (!showDebugOverlay) return null
    return calculateCornerPositions(bounds, curvature, radius)
  }, [bounds, curvature, radius, showDebugOverlay])

  // Create boundary line points (closed loop: NW -> NE -> SE -> SW -> NW)
  const boundaryPoints = useMemo(() => {
    if (!corners) return null
    return [
      corners.nw.toArray() as [number, number, number],
      corners.ne.toArray() as [number, number, number],
      corners.se.toArray() as [number, number, number],
      corners.sw.toArray() as [number, number, number],
      corners.nw.toArray() as [number, number, number], // Close the loop
    ]
  }, [corners])

  // Color based on tile coordinates (helps identify patterns)
  const debugColor = useMemo(() => {
    if (!showDebugOverlay) return 'red'
    // Alternate colors based on x+y parity to visualize checkerboard pattern
    const isEven = (tile.coord.x + tile.coord.y) % 2 === 0
    return isEven ? '#ff4444' : '#44ff44'
  }, [tile.coord.x, tile.coord.y, showDebugOverlay])

  return (
    <group>
      <mesh geometry={geometry} renderOrder={1}>
        <shaderMaterial
          ref={materialRef}
          vertexShader={tiledMorphVertexShader}
          fragmentShader={texturedFragmentShader}
          uniforms={uniforms}
          side={THREE.DoubleSide}
          transparent={false}
          polygonOffset={true}
          polygonOffsetFactor={-1}
          polygonOffsetUnits={-1}
        />
      </mesh>

      {/* Debug overlay: boundary lines */}
      {showDebugOverlay && boundaryPoints && (
        <Line
          points={boundaryPoints}
          color={debugColor}
          lineWidth={2}
        />
      )}

      {/* Debug overlay: tile label at center */}
      {showDebugOverlay && corners && (
        <Text
          position={corners.center.toArray()}
          fontSize={0.3}
          color="yellow"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.02}
          outlineColor="black"
        >
          {tileKey}
        </Text>
      )}
    </group>
  )
})

/**
 * Fallback component while tile texture is loading.
 */
export const TileMeshFallback = memo(function TileMeshFallback({
  tile,
  curvature,
  radius = GLOBE_CONFIG.SPHERE_RADIUS,
  showDebugOverlay = false,
}: TileMeshProps) {
  const tileKey = `${tile.coord.z}/${tile.coord.x}/${tile.coord.y}`
  const materialRef = useRef<THREE.ShaderMaterial>(null)

  // Create geometry ONCE with both position sets (no curvature dependency!)
  const geometry = useMemo(
    () => createTileGeometry(tile.bounds, radius, 4),
    [tile.bounds, radius]
  )

  // Dispose geometry when component unmounts or geometry changes
  // This prevents GPU memory leaks from accumulated geometries
  useEffect(() => {
    return () => {
      geometry.dispose()
    }
  }, [geometry])

  // Color based on loading state
  const color = useMemo(
    () => new THREE.Color(tile.status === 'loading' ? 0x3a4a6e : 0x2a3a4e),
    [tile.status]
  )

  // Create uniforms object - memoized to keep reference stable
  const uniforms = useMemo(
    () => ({
      curvature: { value: curvature },
      color: { value: color },
      opacity: { value: 0.7 },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [color]
  )

  // Update curvature uniform when it changes (no geometry recreation!)
  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.curvature.value = curvature
    }
  }, [curvature])

  // Calculate corner positions for debug overlay
  const corners = useMemo(() => {
    if (!showDebugOverlay) return null
    return calculateCornerPositions(tile.bounds, curvature, radius)
  }, [tile.bounds, curvature, radius, showDebugOverlay])

  // Create boundary line points
  const boundaryPoints = useMemo(() => {
    if (!corners) return null
    return [
      corners.nw.toArray() as [number, number, number],
      corners.ne.toArray() as [number, number, number],
      corners.se.toArray() as [number, number, number],
      corners.sw.toArray() as [number, number, number],
      corners.nw.toArray() as [number, number, number],
    ]
  }, [corners])

  const debugColor = useMemo(() => {
    const isEven = (tile.coord.x + tile.coord.y) % 2 === 0
    return isEven ? '#ff4444' : '#44ff44'
  }, [tile.coord.x, tile.coord.y])

  return (
    <group>
      <mesh geometry={geometry} renderOrder={1}>
        <shaderMaterial
          ref={materialRef}
          vertexShader={basicMorphVertexShader}
          fragmentShader={solidColorWithOpacityFragmentShader}
          uniforms={uniforms}
          side={THREE.DoubleSide}
          transparent
          polygonOffset={true}
          polygonOffsetFactor={-1}
          polygonOffsetUnits={-1}
        />
      </mesh>

      {showDebugOverlay && boundaryPoints && (
        <Line points={boundaryPoints} color={debugColor} lineWidth={2} />
      )}

      {showDebugOverlay && corners && (
        <Text
          position={corners.center.toArray()}
          fontSize={0.3}
          color="cyan"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.02}
          outlineColor="black"
        >
          {tileKey} (loading)
        </Text>
      )}
    </group>
  )
})
